import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { counterDriverFactory } from './Counter.driver';
import { Counter } from '.';
import { act, waitFor } from '@testing-library/react';

describe('Counter', () => {
  const createDriver = createRendererWithUniDriver(counterDriverFactory);
  const ref: React.RefObject<Counter> = React.createRef();
  const errorMessage = 'something here is not ok';
  const defaultProps = {
    inputAriaLabel: 'amount',
    onChange: () => {},
  };

  it('should render', async () => {
    const { driver } = createDriver(
      <Counter inputAriaLabel="amount" onChange={() => {}} />,
    );

    expect(await driver.exists()).toBe(true);
  });

  it('should render "disabled" counter', async () => {
    const { driver } = createDriver(
      <Counter inputAriaLabel="amount" onChange={() => {}} disabled />,
    );

    expect(await driver.isCounterComponentDisabled()).toBe(true);
    expect(await driver.isMinusButtonDisabled()).toBe(true);
    expect(await driver.isPlusButtonDisabled()).toBe(true);
    expect(await driver.isInputComponentDisabled()).toBe(true);
  });

  it('should render "error" counter', async () => {
    const { driver } = createDriver(
      <Counter inputAriaLabel="amount" onChange={() => {}} error />,
    );

    expect(await driver.hasCounterComponentError()).toBe(true);
  });

  it('should render counter with disabled minus button', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        onChange={() => {}}
        min={value}
        value={value}
      />,
    );

    expect(await driver.isMinusButtonDisabled()).toBe(true);
  });

  it('should render counter with disabled plus button', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        onChange={() => {}}
        max={value}
        value={value}
      />,
    );

    expect(await driver.isPlusButtonDisabled()).toBe(true);
  });

  it('should render counter with specific value in input field', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter inputAriaLabel="amount" onChange={() => {}} value={value} />,
    );

    expect(await driver.isInputValueEqualTo(value)).toBe(true);
  });

  it('should call onChange with specific values', async () => {
    let acc;
    const value = 10;
    const step = 1;
    const func = jest.fn((val: string) => {
      acc = Number(val);
    });
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        onChange={func}
        step={step}
        value={value}
      />,
    );

    await driver.pressMinus();
    expect(acc).toEqual(value - step);

    await driver.pressPlus();
    expect(acc).toEqual(value + step);
  });

  it('should call onChange with a typed input', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        onChange={onChange}
        step={1}
        value={0}
      />,
    );

    await driver.enterValue('10');
    expect(onChange).toHaveBeenCalledWith('10');
  });

  it('should have default role attribute and aria-labelledby attribute', async () => {
    const value = 10;
    const counterAriaLabelledBy = 'labelled by testy test';
    const counterAriaLabel = 'testy test';
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        aria-label={counterAriaLabel}
        aria-labelledby={counterAriaLabelledBy}
        onChange={() => {}}
        value={value}
      />,
    );
    expect(await driver.getCounterAriaLabel()).toEqual(counterAriaLabel);
    expect(await driver.getCounterAriaLabellledby()).toEqual(
      counterAriaLabelledBy,
    );
  });

  it('should render tooltip component if in error state and error messsage is defined', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        errorMessage={errorMessage}
        onChange={() => {}}
        value={value}
        error
      />,
    );

    const iconButtonDescription = driver.errorIcon.getIconButtonDescription();
    const tooltipContent = driver.errorIcon.getTooltipContent();

    expect(await driver.hasCounterComponentError()).toBeTruthy();
    expect(await driver.isErrorTooltipExists()).toBeTruthy();
    expect(await iconButtonDescription.text()).toBe(errorMessage);
    expect(await tooltipContent.exists()).toBe(false);

    await driver.errorIcon.click();

    expect(await driver.hasCounterComponentError()).toBeTruthy();
    expect(await driver.isErrorTooltipExists()).toBeTruthy();
    expect(await iconButtonDescription.exists()).toBe(false);
    expect(await tooltipContent.text()).toBe(errorMessage);
  });

  it('should NOT render tooltip component if error messsage is defined but not in error state', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        errorMessage={errorMessage}
        onChange={() => {}}
        value={value}
      />,
    );

    expect(await driver.hasCounterComponentError()).toBeFalsy();
    expect(await driver.isErrorTooltipExists()).toBeFalsy();
  });

  it('should be in error state without tooltip if there is no error message', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        onChange={() => {}}
        value={value}
        error
      />,
    );
    expect(await driver.hasCounterComponentError()).toBeTruthy();
    expect(await driver.isErrorTooltipExists()).toBeFalsy();
  });

  it('should toggle error tooltip when clicking on error icon', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        onChange={() => {}}
        value={value}
        error
        errorMessage={errorMessage}
      />,
    );
    expect(await driver.isErrorTooltipShown()).toBe(false);

    await driver.clickOnErrorIcon();
    expect(await driver.isErrorTooltipShown()).toBe(true);

    await driver.clickOnErrorIcon();
    waitFor(async () => expect(await driver.isErrorTooltipShown()).toBe(false));
  });

  it('should set the correct a11y attributes when old error', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        onChange={() => {}}
        value={value}
        error
        errorMessage={errorMessage}
      />,
    );

    await driver.clickOnErrorIcon();

    expect(await driver.isErrorTooltipExists()).toBeTruthy();
    expect(await driver.isErrorTooltipShown()).toBeTruthy();
    const inputAriaDescribedBy = await driver.getInputAriaDescribedBy();
    const errorIconAriaLabelledBy = await driver.errorIcon.getTooltipId();
    const tooltipContent = driver.errorIcon.getTooltipContent();
    expect(inputAriaDescribedBy).toBeDefined();
    expect(errorIconAriaLabelledBy).toBe(inputAriaDescribedBy);
    expect(await tooltipContent.text()).toBe(errorMessage);
  });

  it('aria describedby should be concatenated to value from props', async () => {
    const value = 10;
    const { driver } = createDriver(
      <Counter
        inputAriaLabel="amount"
        onChange={() => {}}
        value={value}
        error
        errorMessage={errorMessage}
        aria-describedby="some description"
      />,
    );

    await driver.clickOnErrorIcon();
    expect(await driver.isErrorTooltipShown()).toBeTruthy();

    await driver.clickOnErrorIcon();
    expect(await driver.getInputAriaDescribedBy()).toContain(
      'some description',
    );
  });

  it('should set aria-invalid to false if error is not passed', async () => {
    const { driver } = createDriver(<Counter {...defaultProps} />);
    expect(await driver.getAriaInvalid()).toBe(false);
  });

  it('should set aria-invalid based on error prop', async () => {
    const error = true;
    const { driver } = createDriver(
      <Counter {...defaultProps} error={error} />,
    );
    expect(await driver.getAriaInvalid()).toBe(error);
  });

  it('should set aria-valuenow based on value prop', async () => {
    const value = 34;
    const { driver } = createDriver(
      <Counter {...defaultProps} value={value} />,
    );
    expect(await driver.getAriaValueNow()).toBe(value);
  });

  it('should set aria-valuemax based on max prop', async () => {
    const max = 189;
    const { driver } = createDriver(<Counter {...defaultProps} max={max} />);
    expect(await driver.getAriaValueMax()).toBe(max);
  });

  it('should set aria-valuemin based on min prop', async () => {
    const min = 9;
    const { driver } = createDriver(<Counter {...defaultProps} min={min} />);
    expect(await driver.getAriaValueMin()).toBe(min);
  });

  it('should focus the input when the label is clicked', async () => {
    const { driver } = createDriver(<Counter {...defaultProps} label="test" />);

    expect(await driver.isFocused()).toBe(false);
    await driver.getLabel().click();
    expect(await driver.isFocused()).toBe(true);
  });

  it('should display a label with an asterisk when both required and showAsterisk are true', async () => {
    const { driver } = createDriver(
      <Counter {...defaultProps} label="test" required showAsterisk />,
    );
    expect(await driver.getLabelAsterisk().exists()).toBe(true);
  });
  it('should not display a label with an asterisk when only required is true', async () => {
    const { driver } = createDriver(
      <Counter {...defaultProps} label="test" required />,
    );
    expect(await driver.getLabelAsterisk().exists()).toBe(false);
  });

  it('should trigger focus', async () => {
    const { driver } = createDriver(<Counter ref={ref} {...defaultProps} />);

    expect(await driver.isFocused()).toEqual(false);
    await act(async () => ref.current.focus());
    expect(await driver.isFocused()).toEqual(true);
  });

  it('should trigger blur', async () => {
    const { driver } = createDriver(<Counter ref={ref} {...defaultProps} />);

    await act(async () => ref.current.focus());
    expect(await driver.isFocused()).toEqual(true);
    await act(async () => ref.current.blur());
    expect(await driver.isFocused()).toEqual(false);
  });
});
